
import { useState } from 'react';
import { AuthService } from '@/services/authService';
import { toast } from 'sonner';
import { AuthError } from '@/contexts/auth/types';

export const useResetPassword = () => {
  const [isRequestingReset, setIsRequestingReset] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const requestPasswordReset = async (email: string, redirectUrl?: string) => {
    setIsRequestingReset(true);
    setError(null);
    
    try {
      const { error } = await AuthService.resetPassword(email, redirectUrl);
      
      if (error) throw error;
      
      toast.success("Password reset link sent! Please check your email.");
      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to send reset link');
      console.error('Error requesting password reset:', error.message);
      
      setError(error);
      
      toast.error(`Failed to send reset link: ${error.message}`);
      return { error: { message: error.message } as AuthError };
    } finally {
      setIsRequestingReset(false);
    }
  };

  const resetPassword = async (newPassword: string) => {
    setIsResetting(true);
    setError(null);
    
    try {
      const { error } = await AuthService.updatePassword(newPassword);
      
      if (error) throw error;
      
      toast.success("Password reset successfully! You can now log in with your new password.");
      return { error: null };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to reset password');
      console.error('Error resetting password:', error.message);
      
      setError(error);
      
      toast.error(error.message || "Failed to reset password. Please try again.");
      return { error: { message: error.message } as AuthError };
    } finally {
      setIsResetting(false);
    }
  };

  return {
    requestPasswordReset,
    resetPassword,
    isRequestingReset,
    isResetting,
    error
  };
};
